class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener(
      "keyup",
      (evt) => evt.code === "Escape" && this.close()
    );
    this.querySelector("#CartDrawer-Overlay").addEventListener(
      "click",
      this.close.bind(this)
    );
    this.setHeaderCartIconAccessibility();
    this.addEventListener("click", (event) => {
      const detailsToggle = event.target.closest(
        "[data-cart-drawer-price-toggle]"
      );
      if (detailsToggle) {
        this.togglePriceDetails(detailsToggle);
        return;
      }

      const addButton = event.target.closest("[data-cart-drawer-add]");
      if (!addButton) return;
      event.preventDefault();
      this.quickAdd(addButton);
    });
    // show the picked quantity right away; the cart re-renders once the update lands
    this.addEventListener("change", (event) => {
      const qtySelect = event.target.closest("[data-cart-drawer-qty]");
      if (!qtySelect) return;
      const qtyValue = qtySelect
        .closest(".ff-cd__item-qty")
        ?.querySelector(".ff-cd__item-qty-value");
      if (qtyValue) qtyValue.textContent = qtySelect.value;
    });
    this.addEventListener(
      "scroll",
      (event) => {
        if (event.target.classList?.contains("ff-cd__recs-track"))
          this.updateRecsScrollbar();
      },
      true
    );
  }

  togglePriceDetails(toggle) {
    const panel = document.getElementById(
      toggle.getAttribute("aria-controls")
    );
    if (!panel) return;
    const expanded = toggle.getAttribute("aria-expanded") === "true";
    toggle.setAttribute("aria-expanded", String(!expanded));
    panel.hidden = expanded;
  }

  updateRecsScrollbar() {
    const track = this.querySelector(".ff-cd__recs-track");
    const scrollbar = this.querySelector(".ff-cd__recs-scrollbar");
    if (!track || !scrollbar) return;

    // a few px of overflow is just the end gutter, not more cards
    const maxScroll = track.scrollWidth - track.clientWidth;
    scrollbar.hidden = maxScroll <= 8;
    if (scrollbar.hidden) return;

    const visible = track.clientWidth / track.scrollWidth;
    const progress = track.scrollLeft / maxScroll;
    const thumb = scrollbar.querySelector(".ff-cd__recs-thumb");
    thumb.style.width = `${visible * 100}%`;
    thumb.style.transform = `translateX(${progress * (1 / visible - 1) * 100}%)`;
  }

  quickAdd(button) {
    if (button.getAttribute("aria-disabled") === "true") return;
    button.setAttribute("aria-disabled", "true");
    button.classList.add("is-loading");

    const errors = this.querySelector(
      `#${button.dataset.cartDrawerError || "CartDrawer-RecsError"}`
    );
    if (errors) errors.textContent = "";

    const variantId = button.dataset.cartDrawerAdd;
    const body = JSON.stringify({
      items: [{ id: Number(variantId), quantity: 1 }],
      sections: this.getSectionsToRender().map((section) => section.id),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_add_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          if (errors)
            errors.textContent =
              response.description || window.cartStrings.error;
          return;
        }

        this.classList.remove("is-empty");
        this.renderContents(response);
        setTimeout(() =>
          trapFocus(
            document.getElementById("CartDrawer"),
            this.querySelector(".drawer__inner")
          )
        );
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: "cart-drawer",
          productVariantId: variantId,
          cartData: response,
        });
      })
      .catch((e) => {
        console.error(e);
        if (errors) errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        if (!button.isConnected) return;
        button.removeAttribute("aria-disabled");
        button.classList.remove("is-loading");
      });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector("#cart-icon-bubble");
    if (!cartLink) return;

    cartLink.setAttribute("role", "button");
    cartLink.setAttribute("aria-haspopup", "dialog");
    cartLink.addEventListener("click", (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener("keydown", (event) => {
      if (event.code.toUpperCase() === "SPACE") {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute("role"))
      this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add("animate", "active");
      this.updateRecsScrollbar();
    });

    this.addEventListener(
      "transitionend",
      () => {
        const containerToTrapFocusOn = this.classList.contains("is-empty")
          ? this.querySelector(".drawer__inner-empty")
          : document.getElementById("CartDrawer");
        const focusElement =
          this.querySelector(".drawer__inner") ||
          this.querySelector(".drawer__close");
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add("overflow-hidden", "cart-drawer-open");
  }

  close() {
    this.classList.remove("active");
    removeTrapFocus(this.activeElement);
    document.body.classList.remove("overflow-hidden", "cart-drawer-open");
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute("role", "button");
    cartDrawerNote.setAttribute("aria-expanded", "false");

    if (cartDrawerNote.nextElementSibling.getAttribute("id")) {
      cartDrawerNote.setAttribute(
        "aria-controls",
        cartDrawerNote.nextElementSibling.id
      );
    }

    cartDrawerNote.addEventListener("click", (event) => {
      event.currentTarget.setAttribute(
        "aria-expanded",
        !event.currentTarget.closest("details").hasAttribute("open")
      );
    });

    cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector(".drawer__inner").classList.contains("is-empty") &&
      this.querySelector(".drawer__inner").classList.remove("is-empty");
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    setTimeout(() => {
      this.querySelector("#CartDrawer-Overlay").addEventListener(
        "click",
        this.close.bind(this)
      );
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  getSectionDOM(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("cart-drawer", CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__inner",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ];
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);
